import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { notificationsApi } from '../api/notificationsApi';
import type { Notification, NotificationsResponse } from '../types/notification.types';

/**
 * Query key factory for notifications
 */
const notificationKeys = {
  all: ['notifications'] as const,
  list: (page: number, limit: number) => [...notificationKeys.all, 'list', { page, limit }] as const,
  unreadCount: () => [...notificationKeys.all, 'unreadCount'] as const,
};

/**
 * Hook for fetching paginated notifications
 */
export const useNotifications = (page = 1, limit = 20) => {
  return useQuery<NotificationsResponse, Error>({
    queryKey: notificationKeys.list(page, limit),
    queryFn: () => notificationsApi.getNotifications(page, limit),
    staleTime: 1 * 60 * 1000, // 1 minute
    gcTime: 5 * 60 * 1000,
  });
};

/**
 * Hook for fetching unread notification count
 */
export const useUnreadNotificationCount = () => {
  return useQuery<number, Error>({
    queryKey: notificationKeys.unreadCount(),
    queryFn: () => notificationsApi.getUnreadCount(),
    staleTime: 30 * 1000, // 30 seconds - refresh more frequently
    gcTime: 5 * 60 * 1000,
    refetchInterval: 60 * 1000, // Poll every minute for new notifications
  });
};

/**
 * Hook for marking a single notification as read
 */
export const useMarkNotificationAsRead = () => {
  const queryClient = useQueryClient();

  return useMutation<Notification, Error, string>({
    mutationFn: (id) => notificationsApi.markAsRead(id),
    onSuccess: (updatedNotification) => {
      // Update the notification in any cached list
      queryClient.setQueriesData<NotificationsResponse>(
        { queryKey: notificationKeys.all },
        (oldData) => {
          if (!oldData) return oldData;
          return {
            ...oldData,
            notifications: oldData.notifications.map((n) =>
              n.id === updatedNotification.id ? { ...n, isRead: true } : n
            ),
          };
        }
      );
      // Decrement unread count
      queryClient.setQueryData<number>(notificationKeys.unreadCount(), (old) =>
        old !== undefined ? Math.max(0, old - 1) : 0
      );
    },
  });
};

/**
 * Hook for marking all notifications as read
 */
export const useMarkAllNotificationsAsRead = () => {
  const queryClient = useQueryClient();

  return useMutation<void, Error, void>({
    mutationFn: () => notificationsApi.markAllAsRead(),
    onSuccess: () => {
      // Mark all notifications as read in cache
      queryClient.setQueriesData<NotificationsResponse>(
        { queryKey: notificationKeys.all },
        (oldData) => {
          if (!oldData) return oldData;
          return {
            ...oldData,
            notifications: oldData.notifications.map((n) => ({ ...n, isRead: true })),
          };
        }
      );
      // Set unread count to 0
      queryClient.setQueryData<number>(notificationKeys.unreadCount(), 0);
    },
  });
};

/**
 * Hook that combines all notification functionality
 */
export const useNotificationActions = () => {
  const queryClient = useQueryClient();
  const markAsRead = useMarkNotificationAsRead();
  const markAllAsRead = useMarkAllNotificationsAsRead();

  const refresh = () => {
    queryClient.invalidateQueries({ queryKey: notificationKeys.all });
  };

  return {
    markAsRead: markAsRead.mutateAsync,
    markAllAsRead: markAllAsRead.mutateAsync,
    isMarkingAsRead: markAsRead.isPending,
    isMarkingAllAsRead: markAllAsRead.isPending,
    refresh,
  };
};
